import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import secureLocalStorage from 'react-secure-storage';
import { rememberOwnAuthWrite } from './authCipherMemo';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
export const isSafari = () => {
    const userAgent = navigator.userAgent;
    return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
};

/**
 * @param {number} 원본 화면의 width (px 단위)
 * @param {number} 원본 화면의 height (px 단위)
 * @return {function()} 현재 화면의 크기대로 frame을 조정하는 함수
 */
export const getResizeEventListener = (standardWidth: number, standardHeight: number) => {
    return () => {
        if (isSafari()) {
            // console.log('사파리');
            const root: any = document.querySelector('#root');
            const app: any = document.querySelector('#App');

            // 원하는 해상도로 width, height 고정
            app.style.width = `${standardWidth}px`;
            app.style.height = `${standardHeight}px`;

            let width = root.clientWidth;
            let height = width * (standardHeight / standardWidth);

            // style.transform을 이용하여, 화면을 크기를 조정
            app.style.transform = `scale(${width / standardWidth}, ${height / standardHeight})`;

            if (height > root.clientHeight) {
                height = root.clientHeight;
                width = height * (standardWidth / standardHeight);

                // style.transform을 이용하여, 화면을 크기를 조정
                app.style.transform = `scale(${width / standardWidth}, ${height / standardHeight})`;
            }
        } else {
            // console.log('사파리 아님');
            const root: any = document.querySelector('#root');
            const app: any = document.querySelector('#App');

            // 원하는 해상도로 width, height 고정
            app.style.width = `${standardWidth}px`;
            app.style.height = `${standardHeight}px`;

            let width = root.clientWidth;
            let height = width * (standardHeight / standardWidth);

            // style.zoom을 이용하여, 화면을 크기를 조정
            app.style.zoom = height / standardHeight;

            if (height > root.clientHeight) {
                height = root.clientHeight;
                width = height * (standardWidth / standardHeight);

                // style.zoom을 이용하여, 화면을 크기를 조정
                app.style.zoom = width / standardWidth;
            }
        }
    };
};

/**
 * @param {Dayjs} 현재 날짜
 * @return {function()} 현재 날짜가 속한 주의 주차를 구하는 함수
 */
export const getCurrentDayWeekNumber = (currentDate: Dayjs) => {
    const baseDate = dayjs(currentDate);
    const startWeekNumberDate = baseDate.startOf('month').startOf('week');
    const endWeekNumberDate = baseDate.startOf('week');
    let weekDiff = endWeekNumberDate.diff(startWeekNumberDate, 'day', true);
    let weekNumber = Math.floor(weekDiff) / 7 + 1; // 0주차는 존재할 수 없으니 1을 더해줌

    return weekNumber;
};

/**
 * @param {Dayjs} 현재 날짜
 * @return {function()} 해당 월이 몇주차로 이루어져있는지 ex(4주차, 5주차)
 */
export const getCurrentWeekNumberOfMonth = (currentDate: Dayjs) => {
    const baseDate = dayjs(currentDate);
    const startWeekNumberDate = baseDate.startOf('month').startOf('week');

    // 해당월이 몇주차로 이루어져 있는지
    const endMonthNumberDate = dayjs(currentDate).endOf('month').startOf('week');
    let weekNumbers = endMonthNumberDate.diff(startWeekNumberDate, 'day', true);
    let maxWeekNumberOfMonth = Math.floor(weekNumbers) / 7 + 1;

    return maxWeekNumberOfMonth;
};

export const Row = styled.div`
    display: flex;
    flex-direction: row;
`;

export const Col = styled.div`
    display: flex;
    flex-direction: column;
`;

export const CENTER_VIEW_SESSION_STORAGE_KEY = 'rappo:center-view-session';
export const CENTER_VIEW_TAB_MARKER_KEY = 'rappo:center-view-tab';

/**
 * 라커 비밀번호 전역 공개 토글. 센터마다 따로 두려고 뒤에 centerId 를 붙인다.
 *
 * 세션 저장이라 탭을 닫으면 사라지지만, **로그아웃은 탭을 닫지 않는다.** 계정이나 센터가
 * 바뀌었는데 앞 사람이 켜 둔 공개 상태가 남아 있으면 비밀번호가 그대로 보인다.
 * 그래서 `clearLocalStorage()` 가 이 접두사로 시작하는 키를 모두 지운다.
 */
export const LOCKER_REVEAL_STORAGE_PREFIX = 'rappo:locker-reveal:';

/** 접두사로 시작하는 세션 키를 전부 제거. 센터를 몰라도 지울 수 있어야 한다. */
export const clearLockerRevealSession = () => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(LOCKER_REVEAL_STORAGE_PREFIX)) keys.push(key);
        }
        keys.forEach((key) => sessionStorage.removeItem(key));
    } catch {}
};

export interface CenterViewSessionState {
    authorization: string;
    authObject: any;
    expirationTime: number;
}

const isAuthStorageKey = (key: any) =>
    key === 'authorization' || key === 'authObject' || key === 'refreshToken';

export const isCenterViewTab = () => {
    try {
        return sessionStorage.getItem(CENTER_VIEW_TAB_MARKER_KEY) === 'true';
    } catch {
        return false;
    }
};

export const markCenterViewTab = () => {
    try {
        sessionStorage.setItem(CENTER_VIEW_TAB_MARKER_KEY, 'true');
    } catch {}
};

export const getCenterViewSession = (): CenterViewSessionState | null => {
    try {
        const stored = sessionStorage.getItem(CENTER_VIEW_SESSION_STORAGE_KEY);
        if (!stored) return null;

        const parsed = JSON.parse(stored) as CenterViewSessionState;
        if (!parsed.authorization || !parsed.authObject || Date.now() >= parsed.expirationTime) {
            sessionStorage.removeItem(CENTER_VIEW_SESSION_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        try {
            sessionStorage.removeItem(CENTER_VIEW_SESSION_STORAGE_KEY);
        } catch {}
        return null;
    }
};

export const setCenterViewSession = ({
    authorization,
    authObject,
    expiresAt,
}: {
    authorization: string;
    authObject: any;
    expiresAt?: string | number | Date | null;
}) => {
    markCenterViewTab();
    const parsedExpiration = expiresAt ? new Date(expiresAt).getTime() : NaN;
    const expirationTime = Number.isFinite(parsedExpiration)
        ? parsedExpiration
        : Date.now() + 12 * 60 * 60 * 1000;

    const state: CenterViewSessionState = {
        authorization,
        authObject: {
            ...authObject,
            role: 'center_viewer',
            accessMode: 'view',
        },
        expirationTime,
    };
    sessionStorage.setItem(CENTER_VIEW_SESSION_STORAGE_KEY, JSON.stringify(state));
};

export const clearCenterViewSession = (removeTabMarker = false) => {
    try {
        sessionStorage.removeItem(CENTER_VIEW_SESSION_STORAGE_KEY);
        sessionStorage.removeItem('payrollAuthUnlockedAt');
        if (removeTabMarker) sessionStorage.removeItem(CENTER_VIEW_TAB_MARKER_KEY);
    } catch {}
};

export const isCenterViewMode = () => getCenterViewSession()?.authObject?.accessMode === 'view';

export const shouldBlockCenterViewRequest = (method?: string, readOnlySafe = false) => {
    const normalizedMethod = (method || 'get').toLowerCase();
    return (
        isCenterViewMode() &&
        ['post', 'put', 'patch', 'delete'].includes(normalizedMethod) &&
        !readOnlySafe
    );
};

// 데이터와 만료 시간을 함께 저장하는 함수
export const setItemWithExpiration = (key: any, value: any, expirationInHours: any) => {
    if (isAuthStorageKey(key) && isCenterViewTab()) {
        // 열람 탭은 refresh token을 갖지 않으며, 세션 만료 후에도 공용 인증 저장소에 쓰지 않는다.
        if (key === 'refreshToken') return;
        const centerViewSession = getCenterViewSession();
        if (!centerViewSession) return;

        const nextValue =
            key === 'authObject'
                ? {
                      ...centerViewSession.authObject,
                      ...value,
                      role: 'center_viewer',
                      accessMode: 'view',
                  }
                : value;
        const nextSession = {
            ...centerViewSession,
            [key]: nextValue,
            expirationTime: Math.min(
                centerViewSession.expirationTime,
                Date.now() + expirationInHours * 60 * 60 * 1000,
            ),
        };
        sessionStorage.setItem(CENTER_VIEW_SESSION_STORAGE_KEY, JSON.stringify(nextSession));
        return;
    }

    const now = new Date();
    const expirationTime = now.getTime() + expirationInHours * 60 * 60 * 1000; // 현재 시간에 만료 시간을 더함
    const item = { value, expirationTime };
    const serialized = JSON.stringify(item);
    secureLocalStorage.setItem(key, serialized);
    // 인증 값이면 이 탭이 쓴 암호문·평문을 기억한다(지문이 바뀐 뒤에도 자기 저장을 읽기 위해 — lib/authCipherMemo).
    if (isAuthStorageKey(key)) rememberOwnAuthWrite(key, serialized);
};

// 만료 여부를 확인하고 데이터를 가져오는 함수
export const getItemWithExpiration = (key: any) => {
    if (isAuthStorageKey(key) && isCenterViewTab()) {
        if (key === 'refreshToken') return null;
        const centerViewSession = getCenterViewSession();
        return centerViewSession ? centerViewSession[key as 'authorization' | 'authObject'] : null;
    }

    const storedItem: any = secureLocalStorage.getItem(key);

    if (storedItem) {
        try {
            const parsedItem = JSON.parse(storedItem);
            const now = new Date();
            if (now.getTime() < parsedItem.expirationTime) {
                return parsedItem.value;
            } else {
                // 만료된 데이터는 삭제
                secureLocalStorage.removeItem(key);
                window.location.replace('/');
            }
        } catch (error) {
            // JSON 파싱 실패 시 손상된 데이터로 간주하고 삭제
            console.error(`Failed to parse stored item for key "${key}":`, error);
            secureLocalStorage.removeItem(key);
            return null;
        }
    }
    return null; // 데이터가 없거나 만료됐을 경우 null 반환
};

// 로컬스토리지 비우기
export const clearLocalStorage = () => {
    // 라커 비밀번호 공개 상태는 어느 경로로 나가든 남기지 않는다.
    // 센터 열람 탭도 마찬가지다 — 열람 세션이 끝나면 그 센터의 비밀번호도 다시 가려야 한다.
    clearLockerRevealSession();

    if (isCenterViewTab()) {
        // 센터 열람 탭에서의 로그아웃/401은 원래 슈퍼관리자 탭의 인증을 건드리지 않는다.
        clearCenterViewSession(false);
        return;
    }

    secureLocalStorage.clear();
    // 정산 비밀번호 세션도 함께 초기화 (로그아웃 시)
    try {
        sessionStorage.removeItem('payrollAuthUnlockedAt');
    } catch {}
};

// 문자열을 받아 최대길이만큼 자르고 + '...' 붙이는함수
export const truncateString = (str: string, maxLength: number) => {
    if (str?.length > maxLength) {
        return str.slice(0, maxLength) + '...';
    } else {
        return str;
    }
};

export const convertDate = (currentDate: Dayjs, format: string) => {
    const convertDateString = dayjs(currentDate).format(format);
    return convertDateString;
};

// #region FRONT_LEFT_NECT 글자 바꾸기 -> 앞면, 목, 왼쪽
export const translatePart = (part: Part): string => {
    const partsDictionary: { [key in Part]?: string } = {
        HEAD: '머리',
        NECT: '목',
        SHOULDER: '어깨',
        ELBOW: '팔꿈치',
        WRIST: '손목',
        HAND: '손',
        CHEST: '가슴',
        BACK: '등',
        SPINE: '척추',
        WAIST: '허리',
        ABDOMEN: '복부',
        PELVIS: '골반',
        HIPJOINT: '고관절',
        THIGHS: '허벅지',
        HAMSTRING: '뒷허벅지',
        KNEE: '무릎',
        SHANK: '정강이',
        CALF: '종아리',
        ANKLE: '발목',
        FOOT: '발',
        UPPERSPINE: '상부척추',
        LOWERSPINE: '하부척추',
        TAILBONE: '꼬리뼈',
    };

    return partsDictionary[part] ?? part;
};

export const translateDirection = (direction: Direction): string => {
    const directionDictionary: { [key in Direction]?: string } = {
        FRONT: '앞면',
        BACK: '뒷면',
    };

    return directionDictionary[direction] ?? direction;
};

export const translatePosition = (position: Position): string => {
    const positionDictionary: { [key in Position]?: string } = {
        LEFT: '왼쪽',
        CENTER: '가운데',
        RIGHT: '오른쪽',
    };
    return positionDictionary[position] ?? position;
};

export const splitAndTranslate = (inputString: string): string[] => {
    const parts = inputString.split('_');

    // Adjust based on your actual pattern, if needed
    if (parts.length === 3) {
        const [direction, position, part] = parts;
        return [
            translateDirection(direction as Direction),
            translatePosition(position as Position),
            translatePart(part as Part),
        ];
    }

    // Return the original string in an array if it doesn't match the pattern
    return [inputString];
};

export const isSameDate = (firstDate: Dayjs, secondDate: Dayjs) => {
    if (dayjs(firstDate).format('YYYY-MM-DD HH:mm') === dayjs(secondDate).format('YYYY-MM-DD HH:mm')) {
        return true;
    } else {
        return false;
    }
};

export const concatDateAndTimeDayjs = (_date: any, _time: any) => {
    const date = dayjs(_date).format('YYYY-MM-DD');
    const time = dayjs(_time).format('HH:mm:ss');

    const result = date + ' ' + time;

    return dayjs(result);
};
/**
 *
 * @brief data 파라미터로 받은 객체의 값이 undefined나 null이 아닌 경우에만 data 객체에 추가하는 함수
 * @example
 *  const data = await createDataObject({
 *   name,
 *   gender,
 *   birthday,
 *   phoneNumber,
 *   memo,
 *   newCoachId,
 * });
 */
export const createDataObject = async (params: { [s: string]: unknown } | ArrayLike<unknown>) => {
    let data: { [key: string]: any } = {};

    for (const [key, value] of Object.entries(params)) {
        // 값이 undefined, null, 빈 문자열, 빈 배열이 아닌 경우에만 추가
        if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
            data[key] = value;
        }
    }

    return data;
};

type Direction = 'FRONT' | 'BACK';
type Position = 'LEFT' | 'CENTER' | 'RIGHT';
type Part =
    | 'HEAD'
    | 'NECT'
    | 'SHOULDER'
    | 'ELBOW'
    | 'WRIST'
    | 'HAND'
    | 'CHEST'
    | 'BACK'
    | 'SPINE'
    | 'WAIST'
    | 'ABDOMEN'
    | 'PELVIS'
    | 'HIPJOINT'
    | 'THIGHS'
    | 'HAMSTRING'
    | 'KNEE'
    | 'SHANK'
    | 'CALF'
    | 'ANKLE'
    | 'FOOT'
    | 'UPPERSPINE'
    | 'LOWERSPINE'
    | 'TAILBONE';

export const convertHourMin = (_str: string | undefined | Dayjs, format: 'dot' | 'text', isAmPm: boolean) => {
    const date = dayjs(_str);
    let hour = date.hour();
    const min = date.format('mm');

    let meridiem = '';
    if (isAmPm) {
        if (hour < 12) {
            meridiem = '오전';
        } else {
            meridiem = '오후';
            if (hour > 12) {
                hour -= 12;
            }
        }
        if (hour === 0) {
            hour = 12;
        }
    }

    const formattedTime = format === 'dot' ? `${hour.toString().padStart(2, '0')}:${min}` : `${hour}시 ${min}분`;

    return isAmPm ? `${meridiem} ${formattedTime}` : formattedTime;
};

// 한국 돈으로 변환
export const convertToKRW = (price: number) => {
    return price?.toLocaleString('ko-KR') ?? 0;
};

export const isEmptyString = (value: any) => {
    return value === '' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
};

// 0,10,20,30,40 : 불만족 😡
// 50,60,70 : 보통 😐
// 80,90,100 : 만족 ✅
export const getSatisfactionReturnEmojiText = (score: number): { emoji: string; text: string; svgIconName: string } => {
    if (score >= 0 && score < 50) {
        return { emoji: '😡', text: '불만족', svgIconName: 'SvgSatisfactionBad' };
    } else if (score >= 50 && score < 80) {
        return { emoji: '😐', text: '보통', svgIconName: 'SvgSatisfactionNeutral' };
    } else if (score >= 80 && score <= 100) {
        return { emoji: '✅', text: '만족', svgIconName: 'SvgSatisfactionSatisfied' };
    }
    return { emoji: '', text: '', svgIconName: '' };
};

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// 0과 undefined,null 분리 하는 함수
export const isZeroOrDefined = (value: any): boolean => {
    return value === 0 || (value !== undefined && value !== null);
};
export const formatTimeWithAMPM = (date: string | Date | dayjs.Dayjs) => {
    if (!date) return '';
    const d = dayjs(date);
    const hour = d.hour();
    const minute = d.minute();
    const ampm = hour >= 12 ? '오후' : '오전';
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    const formattedMinute = minute.toString().padStart(2, '0');
    return `${ampm} ${formattedHour}:${formattedMinute}`;
};

/* ------------------------------------------------------------------
 * 금액 입력 콤마 표시
 *
 * 표시에만 콤마를 붙이고, state·부모 콜백·payload 에는 콤마 없는 숫자만 흘린다.
 * `parseInt('1,000')` 은 에러 없이 1 을 돌려주므로 이 경계가 무너지면 돈이 틀어진다.
 *
 * 잘못된 입력은 "지우지" 말고 "거부"한다. 비숫자를 걷어내는 방식은 실패 대신
 * 다른 유효 금액을 만들어낸다 — `-100` → 100, `12.34` → 1234(100배), `1e3` → 13.
 * 기존 숫자 입력이 쓰던 "패턴에 안 맞으면 값을 바꾸지 않는다"를 그대로 잇는다.
 * ------------------------------------------------------------------ */

/**
 * 금액 입력이 허용하는 문법. 숫자·콤마·소수점 한 개를 통과시킨다.
 *
 * **소수점을 막지 않는다.** 콤마는 보이는 것만 바꾸는 장치이고, 그것 때문에 원래 되던
 * 입력이 안 되면 안 된다. 변경 전 `type="number"` 였던 화면들은 `10.5` 가 입력됐고,
 * 제출부의 `parseInt`/`Number` 가 각자 방식대로 처리했다. 그 동작을 그대로 잇는다.
 *
 * 콤마 위치는 검사하지 않는다. `1,000` 에서 한 글자를 지우면 `1,00` 이라는
 * **정상적인 편집 중간 상태**가 생기는데, 정규형만 받으면 그게 거부되어 지울 수가 없다.
 * 대신 한 번에 값이 확정되는 붙여넣기·드롭은 `isPasteableAmount` 로 따로 막는다.
 */
export const AMOUNT_INPUT_PATTERN = /^[0-9,]*\.?[0-9]*$/;

/**
 * 금액 최대 자릿수. `Number` 의 안전 정수(2^53, 16자리) 안에 머물게 한다.
 * 넘어가면 화면 문자열은 정확한데 payload 는 반올림되어 둘이 달라진다.
 */
export const MAX_AMOUNT_DIGITS = 15;

/** 정수부 자릿수. 상한은 소수부를 빼고 센다. */
const integerDigitCount = (text: string) => {
    const raw = stripAmountComma(text);
    const dot = raw.indexOf('.');
    return (dot === -1 ? raw : raw.slice(0, dot)).replace(/[^0-9]/g, '').length;
};

/** 소수점을 뺀 정수 전용 문법. 변경 전에 `/^\\d*$/` 로 정수만 받던 화면이 쓴다. */
const AMOUNT_INPUT_PATTERN_INTEGER = /^[0-9,]*$/;

/**
 * 타이핑·삭제로 들어온 값을 받아도 되는지. 문법과 자릿수 상한을 함께 본다.
 *
 * `allowDecimal` 은 **화면마다 변경 전 동작이 달라서** 있는 것이다. 콤마를 넣는다고
 * 원래 되던 소수 입력을 막아도 안 되고, 원래 정수만 받던 칸을 열어도 안 된다.
 * 정수 칸을 열면 `parseInt('10.5')` 가 10 이라 **화면과 payload 가 어긋난다.**
 */
export const isAmountInputAllowed = (raw: string, allowDecimal = false) => {
    if (!(allowDecimal ? AMOUNT_INPUT_PATTERN : AMOUNT_INPUT_PATTERN_INTEGER).test(raw)) return false;
    if (integerDigitCount(raw) > MAX_AMOUNT_DIGITS) return false;
    /*
     * 소수점만 있는 `.` 같은 값은 문법은 맞지만 `Number('.')` 가 NaN 이다.
     * 제출부는 대부분 `Number(state)` 를 그대로 보내므로 NaN 이 서버까지 간다.
     * 변경 전 `type="number"` 는 이런 입력을 빈 문자열로 돌려 `Number('')` 가 0 이었다.
     */
    const digits = stripAmountComma(raw);
    return digits === '' || !Number.isNaN(Number(digits));
};

/**
 * 한 번에 값이 확정되는 삽입 유형(Input Events level 2).
 *
 * 붙여넣기·드롭만 있는 게 아니다. 브라우저 자동완성, 맞춤법·제안 교체(`insertReplacementText`),
 * 음성 입력이 전부 여기 속하고 `onPaste`/`onDrop` 을 거치지 않는다.
 * 이 경로들은 편집 중간 상태가 아니므로 느슨한 문법을 쓰면 안 된다.
 */
const BULK_INSERT_INPUT_TYPES = new Set([
    'insertFromPaste',
    'insertFromPasteAsQuotation',
    'insertFromDrop',
    'insertReplacementText',
    'insertFromYank',
]);

/**
 * onChange 단계의 최종 관문. **입력 출처로 잣대를 나눈다.**
 *
 * - 타이핑·삭제: 느슨하게. `1,000` 에서 한 글자 지운 `1,00` 을 거부하면 지울 수가 없다.
 * - 한 번에 확정되는 삽입: 정규형만. 여기를 느슨하게 두면 `10000,20000` 이 통과해
 *   `1000020000` 이라는 다른 금액이 되고, 기프티콘 한도처럼 자동 저장되는 화면에서는
 *   사용자가 알아채기 전에 서버로 넘어간다.
 *
 * 변경 전 이 입력들은 `/^\d*$/` 이거나 `type="number"` 라 콤마가 아예 못 들어왔다.
 * 콤마를 허용하면서 약해진 방어를 여기서 되돌린다.
 *
 * `inputType` 하나만 믿지 않는다. Safari 음성 입력처럼 그 값이 비는 브라우저가 있어서,
 * 자릿수가 한 번에 늘었는지(`bulkByDelta`)를 두 번째 축으로 함께 본다. 아래 주석 참고.
 */
/**
 * `prev` 에서 한 글자를 넣거나 빼서 `next` 가 되는가.
 *
 * 정상적인 타이핑·삭제는 언제나 이 성질을 갖는다. 반대로 붙여넣기·음성 입력·자동완성처럼
 * 통째로 들어온 값은 이 성질이 깨진다.
 *
 * **자릿수 증가량만 보면 안 된다.** 선택 영역을 덮어쓰면 여러 글자가 한 번에 바뀌면서도
 * 전체 길이는 그대로거나 하나만 늘 수 있다. 실제로 기존 값이 9~11자리일 때
 * `10000,20000` 으로 교체하면 증가량이 +1/0/-1 이라 그 검사를 통과해 버렸다.
 */
/**
 * `next` 가 `prev` 에서 **연속된 한 구간만 지워서** 만들어지는가.
 * 백스페이스·Delete·선택 삭제의 성질이다.
 *
 * 길이 차이를 1 로 묶으면 **전체 선택 삭제와 여러 글자 삭제가 막힌다.**
 * 실제로 `12,345` 를 전부 선택해 지워도 값이 그대로 남았다(교차검증 실측).
 * 반대로 콤마 위치가 통째로 다른 교체는 연속 구간 제거로 만들 수 없어 여기서 걸러진다.
 */
const isDeletion = (prev: string, next: string) => {
    if (next.length >= prev.length) return false;
    let i = 0;
    while (i < next.length && next[i] === prev[i]) i += 1;
    return next.slice(i) === prev.slice(prev.length - (next.length - i));
};

const isSingleCharEdit = (prev: string, next: string) => {
    if (prev === next) return true;
    const [shorter, longer] = prev.length < next.length ? [prev, next] : [next, prev];
    if (longer.length - shorter.length !== 1) return false;
    let i = 0;
    while (i < shorter.length && shorter[i] === longer[i]) i += 1;
    return shorter.slice(i) === longer.slice(i + 1);
};

/**
 * onChange 단계의 최종 관문. **입력 출처로 잣대를 나눈다.**
 *
 * - 한 글자 타이핑·삭제: 느슨하게. `1,000` 에서 한 글자 지운 `1,00` 을 거부하면 지울 수가 없다.
 * - 그 밖의 모든 변경: 정규형만. 여기를 느슨하게 두면 `10000,20000` 이 통과해
 *   `1000020000` 이라는 다른 금액이 되고, 기프티콘 한도처럼 자동 저장되는 화면에서는
 *   사용자가 알아채기 전에 서버로 넘어간다.
 *
 * 변경 전 이 입력들은 `/^\d*$/` 이거나 `type="number"` 라 콤마가 아예 못 들어왔다.
 * 콤마를 허용하면서 약해진 방어를 여기서 되돌린다.
 *
 * `inputType` 하나만 믿지 않는다. Safari 음성 입력처럼 그 값이 비는 브라우저가 있어서,
 * 변경의 **모양**(한 글자 편집인지)을 두 번째 축으로 함께 본다.
 */
export const isAmountChangeAllowed = (
    raw: string,
    inputType?: string | null,
    prevText?: string | number,
    allowDecimal = false,
) => {
    const bulkByType = !!inputType && BULK_INSERT_INPUT_TYPES.has(inputType);
    /*
     * **콤마를 뺀 숫자끼리 비교하면 안 된다.** 콤마 위치가 통째로 달라진 교체가
     * 숫자만 보면 한 글자 삽입·삭제로 보이기 때문이다. 실제로 `100002000` 을
     * `10000,20000` 으로 교체하면 숫자는 끝에 0 하나가 붙은 관계라 통과해 버렸다.
     * 화면에 보이던 문자열 그대로 비교해야 콤마가 움직인 것이 드러난다.
     */
    const prevDisplay = formatAmountText(prevText ?? '');
    const bulkByShape = !isSingleCharEdit(prevDisplay, raw) && !isDeletion(prevDisplay, raw);
    return bulkByType || bulkByShape
        ? isPasteableAmount(raw, allowDecimal)
        : isAmountInputAllowed(raw, allowDecimal);
};

/**
 * 붙여넣기·드롭으로 들어오는 값은 **정규형만** 받는다.
 *
 * 편집 중간 상태와 달리 한 번에 값이 확정되므로 느슨하게 두면 안 된다.
 * `10000,20000` 같은 CSV 조각이 통과하면 콤마를 걷어낸 `1000020000` 이라는
 * **전혀 다른 금액**이 만들어지고, 기프티콘 한도처럼 자동 저장되는 화면에서는
 * 사용자가 알아채기 전에 서버로 넘어간다.
 */
export const isPasteableAmount = (text: string, allowDecimal = false) => {
    // 앞뒤 공백을 trim 하지 않는다. 공백째 통과시켜도 브라우저가 삽입한 뒤
    // onChange 의 isAmountInputAllowed 가 공백을 거부해 결국 아무 일도 안 일어난다.
    // 통과시켜 놓고 뒤에서 막으면 코드를 읽는 사람이 오해하므로 여기서 명확히 막는다.
    const value = String(text ?? '');
    if (value === '') return false;
    const shape = allowDecimal ? /^(\d+|\d{1,3}(,\d{3})*)(\.\d+)?$/ : /^(\d+|\d{1,3}(,\d{3})*)$/;
    if (!shape.test(value)) return false;
    return integerDigitCount(value) <= MAX_AMOUNT_DIGITS;
};

/** 표시 문자열에서 콤마만 걷어낸다. 다른 문자는 애초에 통과하지 못한다. */
export const stripAmountComma = (text: string | number) => String(text ?? '').replace(/,/g, '');

/**
 * 금액 표시용. 숫자 문자열에 3자리 콤마를 찍는다.
 *
 * 앞자리 0 은 그대로 둔다. 입력 도중에 지우면 글자 수가 줄어 커서가 앞으로 밀리므로,
 * 이 레포의 숫자 입력은 "05" 같은 표기를 blur 까지 유지하는 계약을 갖고 있다.
 * (NumberTextInput.tsx:7, RadiusTextInput.tsx:25)
 */
export const formatAmountText = (digits: string | number) => {
    const raw = stripAmountComma(digits);
    if (raw === '') return '';
    // 소수부는 건드리지 않는다. 콤마는 정수부에만 찍는다.
    const dot = raw.indexOf('.');
    const head = dot === -1 ? raw : raw.slice(0, dot);
    const tail = dot === -1 ? '' : raw.slice(dot);
    if (head === '' || /^0\d/.test(head)) return raw;
    return head.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + tail;
};

/**
 * 콤마가 끼어들어도 커서가 끝으로 튀지 않게, "커서 앞의 숫자 개수" 로 위치를 되돌린다.
 * 자릿수가 늘어 콤마가 하나 더 생겨도 사용자가 보던 자리에 그대로 머문다.
 */
export const caretPosForDigitCount = (text: string, digitCount: number) => {
    if (digitCount <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < text.length; i += 1) {
        if (text[i] !== ',') seen += 1;
        if (seen === digitCount) return i + 1;
    }
    return text.length;
};

/**
 * 커서 앞의 "콤마를 뺀 글자 수". 커서 위치를 콤마와 무관한 좌표로 환산한다.
 *
 * **콤마만 빼야 한다.** 소수점까지 빼면 `caretPosForDigitCount` 와 대칭이 깨진다.
 * 그쪽은 콤마만 건너뛰며 세기 때문에, `10.` 에서 커서가 `10|.` 로 복원되고
 * 다음 글자가 소수점 **앞**에 끼어들어 `10.5` 를 치면 `105.` 가 됐다.
 */
export const digitCountBefore = (text: string, caretPos: number) =>
    text.slice(0, caretPos).replace(/,/g, '').length;
